import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {RelativeTime} from '@primer/react'
import {type FC, useMemo} from 'react'
import styles from './FindingCommentPreview.module.css'
import type {CommentPreviewData} from './types'

interface FindingCommentPreviewProps {
  preview: CommentPreviewData
  filePath?: string
}

export const FindingCommentPreview: FC<FindingCommentPreviewProps> = ({preview, filePath}) => {
  const {author, bodyHTML, url, createdAt, diffHunk, suggestion} = preview

  const suggestionDiffEntries = suggestion?.diffEntries ?? []
  const diffHunkLines = useMemo(() => (diffHunk ? parseUnifiedDiff(diffHunk) : []), [diffHunk])

  return (
    <div className="d-flex flex-column gap-2" data-testid="finding-comment-preview">
      <div className="d-flex flex-items-center flex-wrap gap-2">
        <GitHubAvatar alt={`@${author.login}`} size={20} src={author.avatarUrl} />
        <span className="text-semibold text-small">
          {url ? (
            <a href={url} className="Link--primary">
              {author.login}
            </a>
          ) : (
            author.login
          )}
        </span>
        {createdAt && (
          <span className="color-fg-muted text-small">
            {url ? (
              <a href={url} className="Link--secondary">
                <RelativeTime datetime={createdAt} />
              </a>
            ) : (
              <RelativeTime datetime={createdAt} />
            )}
          </span>
        )}
      </div>

      <div className="markdown-body tmp-mb-n2">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */}
        <VerifiedHTMLText html={bodyHTML as SafeHTMLString} />
      </div>

      {(diffHunkLines.length > 0 || suggestionDiffEntries.length > 0) && (
        <div className="d-flex flex-column gap-3">
          {diffHunkLines.length > 0 && (
            <div data-testid="finding-comment-diff-hunk">
              <div className="d-flex flex-column overflow-hidden border rounded-2">
                {filePath && (
                  <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                    <span>{filePath}</span>
                  </div>
                )}
                <StaticUnifiedDiffPreview
                  tabSize={4}
                  className={styles.diffPreview}
                  diffTableClassName="borderColor-default"
                  diffLines={diffHunkLines}
                />
              </div>
            </div>
          )}

          {suggestionDiffEntries.map((entry, i) => (
            <div
              key={`${entry.path}-${i}`}
              className="d-flex flex-column gap-2"
              data-testid="finding-comment-suggestion"
            >
              <h4 className="text-small text-semibold color-fg-muted m-0">Suggested change</h4>
              <div className="d-flex flex-column overflow-hidden border rounded-2">
                <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                  <span>{entry.path}</span>
                </div>
                <StaticUnifiedDiffPreview
                  tabSize={4}
                  className={styles.diffPreview}
                  diffTableClassName="borderColor-default"
                  diffLines={entry.diffLines}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
